import { get } from 'lodash'

import { getString, isEmpty } from '@sigma-frontend-libs/commons'
import { isUUID } from '@sigmasms/commons'
import { OrderDirections } from '@sigmasms/shared-types'

import { cachedItems } from '../cache'

/**
 * Локальное состояние выбранных элементов.
 *
 * @typedef {object} SelectedItemsState
 * @property {Array<object>} selectedItems Выбранные элементы в порядке
 * вывода.
 * @property {number} _selectedItemsHydrationRevision Номер текущей сборки
 * списка. По нему отбрасываются устаревшие результаты догрузки.
 */

/**
 * Выбранные элементы выпадающего списка: сборка по выбранным
 * значениям, догрузка недостающих элементов из сервиса и их
 * сортировка.
 */
export const selectedItems = {
  props: {
    /**
     * Направление сортировки выбранных элементов по заголовку.
     * Принимает одно из значений OrderDirections. Если не задано,
     * порядок элементов повторяет порядок выбранных значений.
     *
     * @type {string}
     * @default undefined
     */
    order: {
      type: String,
      default: undefined,
      validator: value => {
        return Object.values(OrderDirections).includes(value)
      }
    }
  },

  /**
   * Локальное состояние выбранных элементов.
   *
   * @returns {SelectedItemsState} Локальное состояние.
   */
  data() {
    return {
      selectedItems: [],

      _selectedItemsHydrationRevision: 0
    }
  },

  watch: {
    /**
     * Отслеживает изменения выбранных значений с глубоким
     * сравнением и пересобирает список выбранных элементов.
     */
    proxy: {
      handler() {
        this._fillSelectedItems()
      },
      deep: true
    }
  },

  methods: {
    /**
     * Отдает копию списка выбранных элементов в нужном порядке.
     * Если задано свойство order, элементы сортируются по заголовку
     * с учетом чисел в указанном направлении. Иначе порядок
     * элементов сохраняется.
     *
     * @param {Array<object>} selectedItems Выбранные элементы.
     * @returns {Array<object>} Упорядоченные выбранные элементы.
     */
    _sortSelectedItems(selectedItems) {
      const result = [ ...selectedItems ]

      if (this.order) {
        const directions = {
          [OrderDirections.ASC]: 1,
          [OrderDirections.DESC]: -1
        }

        result.sort((a, b) => {
          const titleA = String(get(a, this.itemTitle, ''))
          const titleB = String(get(b, this.itemTitle, ''))

          return directions[this.order] * titleA.localeCompare(
            titleB,
            undefined,
            { numeric: true }
          )
        })
      }

      return result
    },

    /**
     * Собирает выбранный элемент по его значению.
     * Источником служит элемент из текущего списка, а при его
     * отсутствии - элемент из общего кеша; если ни того, ни
     * другого нет, используется само значение.
     * Если источник не найден и задано свойство service и значение
     * является идентификатором UUID, элемент помечается как
     * требующий догрузки, иначе признак догрузки снимается.
     *
     * @param {*} value Выбранное значение.
     * @returns {object} Выбранный элемент.
     */
    _createSelectedItem(value) {
      const displayedItem = this.displayItems.find(item => {
        return this._compareValues(item, value)
      })
      const cachedItem = cachedItems.get(getString(value))

      let sourceItem = displayedItem
      if (!sourceItem) {
        sourceItem = cachedItem
      }

      let itemToClear = value
      if (!isEmpty(sourceItem)) {
        itemToClear = sourceItem
      }
      const result = { ...this._clearItem(itemToClear) }

      if (!sourceItem && this.service && isUUID(value)) {
        result._loading = true
      } else {
        result._loading = false
      }

      return result
    },

    /**
     * Догружает выбранный элемент из сервиса.
     * Если элемент не помечен как требующий догрузки, он отдается
     * без изменений. Если сервис вернул элемент, тот приводится к
     * единому виду и сохраняется в общем кеше. Если сервис ничего
     * не вернул, отдается исходный элемент со снятым признаком
     * догрузки.
     *
     * @param {object} selectedItem Выбранный элемент.
     * @returns {Promise<object>} Выбранный элемент после догрузки.
     */
    async _hydrateSelectedItem(selectedItem) {
      if (!selectedItem._loading) {
        return selectedItem
      }

      const value = get(selectedItem, this.$_itemValue, selectedItem)
      const response = await this.requestItem(value)

      if (response) {
        const result = this._clearItem(response)
        cachedItems.set(getString(value), result)

        return result
      }

      return {
        ...selectedItem,
        _loading: false
      }
    },

    /**
     * Пересобирает список выбранных элементов по выбранным
     * значениям.
     *
     * Сначала список заполняется элементами, собранными из текущего
     * списка и общего кеша. Если догружать нечего, список сразу
     * выводится в нужном порядке и работа заканчивается.
     *
     * Иначе недостающие элементы догружаются из сервиса. Если за
     * время догрузки список пересобирали заново, результат
     * отбрасывается. Элементы, догрузка которых завершилась
     * ошибкой, остаются в исходном виде со снятым признаком
     * догрузки.
     */
    async _fillSelectedItems() {
      this._data._selectedItemsHydrationRevision += 1
      const selectedItemsHydrationRevision = this._data._selectedItemsHydrationRevision

      const immediateSelectedItems = this.proxy.map(value => {
        return this._createSelectedItem(value)
      })

      const hasItemsToHydrate = immediateSelectedItems.some(({ _loading }) => {
        return _loading
      })

      if (!hasItemsToHydrate) {
        this.selectedItems.splice(0, this.selectedItems.length, ...this._sortSelectedItems(immediateSelectedItems))

        return
      }

      this.selectedItems.splice(0, this.selectedItems.length, ...immediateSelectedItems)

      const hydratedSelectedItems = await Promise.allSettled(
        immediateSelectedItems.map(selectedItem => {
          return this._hydrateSelectedItem(selectedItem)
        })
      )

      if (selectedItemsHydrationRevision !== this._data._selectedItemsHydrationRevision) {
        return
      }

      const result = hydratedSelectedItems.map((selectedItemResult, index) => {
        if (selectedItemResult.status === 'fulfilled') {
          return selectedItemResult.value
        }

        return {
          ...immediateSelectedItems[index],
          _loading: false
        }
      })

      this.selectedItems.splice(0, this.selectedItems.length, ...this._sortSelectedItems(result))
    },

    /**
     * Отдает заголовок выбранного элемента по его значению.
     * Если значение не задано, отдается undefined. Если элемент
     * найден, отдается его заголовок, а при отсутствии заголовка -
     * строковое представление значения. Если элемент не найден,
     * отдается undefined.
     *
     * @param {object|*} value Выбранный элемент или его значение.
     * @returns {string|undefined} Заголовок выбранного элемента.
     */
    _findTitleInSelectedItemsByValue(value) {
      if (value === undefined) {
        return undefined
      }

      const item = this.selectedItems.find(selectedItem => {
        const selectedItemValue = get(selectedItem, this.$_itemValue, selectedItem)
        const itemValue = get(value, this.$_itemValue, value)

        return this._compareValues(selectedItemValue, itemValue)
      })
      if (item) {
        const itemValue = get(item, this.$_itemValue, item)
        const itemTitle = this._getTitleFromItem(item) || itemValue

        return getString(itemTitle)
      }
    }
  }
}
